import React, { useEffect, useState } from 'react';
import {
  CheckCircle as CheckIcon,
  Feedback as FeedbackIcon,
  Science as ScienceIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { AnimatePresence, motion } from 'motion/react';

import {
  type LabOrderSuggestionPayload,
  type RejectionModalContentProps,
  RejectionReason,
} from '../../types';

// Step definitions for the multi-step flow
type ModalStep = 'initial' | 'feedback' | 'confirmation';

/**
 * Lab order suggestion rejection modal content component
 * Provides standard rejection options
 */
export const LabOrderSuggestionRejection: React.FC<RejectionModalContentProps> = ({
  context,
  onReject,
  onCancel,
  isLoading = false,
}) => {
  const { action } = context;
  const [currentStep, setCurrentStep] = useState<ModalStep>('initial');
  const [feedbackText, setFeedbackText] = useState('');
  const [autoCloseCountdown, setAutoCloseCountdown] = useState<number | null>(null);

  const labOrderData = action.action_payload as LabOrderSuggestionPayload;
  const labOrder = labOrderData.lab_order;
  const testsCount = labOrder.lab_tests.length;

  // Auto-close countdown for confirmation step
  useEffect(() => {
    if (currentStep === 'confirmation' && autoCloseCountdown === null) {
      setAutoCloseCountdown(5);
    }
  }, [currentStep, autoCloseCountdown]);

  useEffect(() => {
    if (autoCloseCountdown !== null && autoCloseCountdown > 0) {
      const timer = setTimeout(() => {
        setAutoCloseCountdown(autoCloseCountdown - 1);
      }, 1000);
      return () => {
        clearTimeout(timer);
      };
    } else if (autoCloseCountdown === 0) {
      // When countdown reaches 0, trigger rejection
      onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
    }
  }, [autoCloseCountdown, onReject]);

  const handleReasonSelect = (reason: RejectionReason) => {
    switch (reason) {
      case RejectionReason.WRONG_SUGGESTION:
        // For lab orders, wrong suggestion means reject immediately
        onReject(RejectionReason.WRONG_SUGGESTION);
        break;
      case RejectionReason.DONT_CARE_FOR_FEATURE:
        setCurrentStep('confirmation');
        break;
      case RejectionReason.OTHER:
        setCurrentStep('feedback');
        break;
    }
  };

  const handleFeedbackSubmit = () => {
    onReject(RejectionReason.OTHER, {
      feedback: feedbackText.trim() || undefined,
    });
  };

  const handleBack = () => {
    setCurrentStep('initial');
    setFeedbackText('');
    setAutoCloseCountdown(null);
  };

  // Type guard to ensure we have lab order data
  if (action.action_type !== 'lab_order_suggestion') {
    console.error('LabOrderSuggestionRejection received non-lab-order action');
    return null;
  }

  return (
    <AnimatePresence mode="popLayout">
      <motion.div
        key={currentStep}
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          layout: { duration: 0.3, ease: 'easeInOut' },
          opacity: { duration: 0.1 },
        }}
        style={{ width: '100%' }}
      >
        {currentStep === 'initial' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
              {/* Lab Order Summary */}
              <Alert severity="info" icon={<ScienceIcon fontSize="inherit" />}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', color: 'primary.dark' }}
                  >
                    Suggested lab order:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.dark', mt: 0.5 }}>
                    {testsCount} test{testsCount !== 1 ? 's' : ''} including{' '}
                    {labOrder.bundle_names.join(', ')}
                  </Typography>
                </Box>
              </Alert>

              {/* Rejection Reason Options */}
              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.WRONG_SUGGESTION);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ScienceIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        These lab tests are not appropriate
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        The suggested tests don&apos;t match what I want to order
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.DONT_CARE_FOR_FEATURE);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SettingsIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        I don&apos;t want lab order suggestions
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Turn off lab order suggestions for all future notes
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>

              <Card variant="outlined" sx={{ cursor: 'pointer' }}>
                <CardActionArea
                  onClick={() => {
                    handleReasonSelect(RejectionReason.OTHER);
                  }}
                  disabled={isLoading}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FeedbackIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" color="text.primary">
                        Other reason
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Something else prevented me from accepting this suggestion
                      </Typography>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={onCancel} disabled={isLoading} variant="outlined">
                Cancel
              </Button>
            </Box>
          </>
        )}

        {currentStep === 'feedback' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <FeedbackIcon color="primary" />
                <Typography variant="h6" color="text.primary">
                  Tell us more
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Help us improve by sharing what prevented you from accepting this lab order
                suggestion.
              </Typography>

              <TextField
                label="Your feedback"
                multiline
                rows={4}
                value={feedbackText}
                onChange={e => {
                  setFeedbackText(e.target.value);
                }}
                placeholder="Please describe why this lab order suggestion wasn't helpful..."
                fullWidth
                variant="outlined"
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button onClick={handleBack} variant="outlined" color="inherit">
                Back
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => {
                    onReject(RejectionReason.OTHER);
                  }}
                  variant="outlined"
                  color="inherit"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleFeedbackSubmit}
                  variant="contained"
                  color="primary"
                  disabled={!feedbackText.trim()}
                >
                  Submit Feedback
                </Button>
              </Box>
            </Box>
          </>
        )}

        {currentStep === 'confirmation' && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                mb: 4,
                textAlign: 'center',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>

              <Typography variant="h6" color="text.primary">
                Lab Order Suggestions Disabled
              </Typography>

              <Typography variant="body1" color="text.secondary">
                You will no longer receive lab order suggestions. You can re-enable this feature
                anytime in your preferences.
              </Typography>

              {autoCloseCountdown !== null && autoCloseCountdown > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Closing in {autoCloseCountdown} seconds...
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                onClick={() => {
                  onReject(RejectionReason.DONT_CARE_FOR_FEATURE);
                }}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
